import type { Character } from "../../../data/schema/character";

const GOLDEN_RATIO_CONJUGATE = 0.61803398875;

/** Shared disc color for every character marked minor. */
export const MINOR_MARKER_COLOR = "rgb(118 126 142)";

export function buildCharacterColorMap(
  characters: Character[],
): ReadonlyMap<string, string> {
  const ordered = characters
    .filter((character) => character.flashbackOf === undefined)
    .sort((a, b) => a.id.localeCompare(b.id));
  const map = new Map<string, string>();
  let hue = 0.07;
  for (const character of ordered) {
    const color = character.markerColor
      ?? (character.minor === true
        ? MINOR_MARKER_COLOR
        : hslToCss(hue, 0.62, 0.56));
    map.set(character.id, color);
    hue = (hue + GOLDEN_RATIO_CONJUGATE) % 1;
  }
  for (const character of characters) {
    const original = character.flashbackOf;
    const color = original === undefined ? undefined : map.get(original);
    if (color !== undefined) {
      map.set(character.id, color);
    }
  }
  return map;
}

export function colorForCharacterFromMap(
  characterId: string,
  colors: ReadonlyMap<string, string>,
): string {
  return colors.get(characterId) ?? "rgb(140 150 170)";
}

function hslToCss(h: number, s: number, l: number): string {
  const [r, g, b] = hslToRgb(h, s, l);
  return `rgb(${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)})`;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => {
    const k = (n + h * 12) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [f(0), f(8), f(4)];
}
